<!-- src/components/data/RouteMap.vue - VERSIONE CORRETTA COMPLETA -->
<template>
  <div class="route-map">
    <div class="map-header">
      <h3>Percorso Veicolo {{ vin ? `- ${vin}` : "" }}</h3>
    </div>

    <div v-if="loading" class="map-loading">
      <div class="spinner"></div>
      <span>Caricamento mappa...</span>
    </div>

    <div v-if="!loading && !locations?.length" class="map-empty">
      <span>⚠️ Nessun dato di posizione disponibile per il periodo selezionato</span>
    </div>

    <div ref="mapContainer" class="map-container" v-show="!loading && locations?.length">
      <!-- Map Controls Overlay -->
      <div class="map-controls-overlay">
        <button @click="fitBounds" class="map-overlay-btn" title="Adatta alla rotta">
          🔍
        </button>
        <button @click="toggleSatellite" class="map-overlay-btn" title="Toggle Satellite">
          {{ isSatellite ? "🗺️" : "🛰️" }}
        </button>
        <button @click="toggleFollow" class="map-overlay-btn" :class="{ 'active': isFollowing }" :title="isFollowing ? 'Smetti di seguire' : 'Centra e Segui'">
          {{ isFollowing ? "🔒" : "🎯" }}
        </button>
      </div>
    </div>

    <div class="map-info">
      <div class="info-item">
        <span class="info-label">Punti tracciamento:</span>
        <span class="info-value">{{ locations.length }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Distanza totale:</span>
        <span class="info-value"
          >{{ calculateTotalDistance().toFixed(2) }} km</span
        >
      </div>
      <div class="info-item">
        <span class="info-label">Ultima posizione:</span>
        <span class="info-value">{{
          formatTimestamp(latestLocation?.timestamp)
        }}</span>
      </div>
    </div>
    
    <div class="timeline-container">
      <div class="timeline-header">
        <div class="timeline-meta">
            <button class="calendar-btn" @click="toggleCalendar" title="Seleziona una data">
                📅
            </button>
        </div>
        <span>{{ playbackIndex + 1 }} / {{ locations.length }}</span>

        <!-- Calendar Popover -->
        <!-- <div v-if="showCalendar" class="calendar-popover">
          <MaterialCalendar
             :model-value="selectedDate"
             :activityDates="activityDates"
             @update:modelValue="onCalendarSelect"
             @close="showCalendar = false"
          />
        </div> -->
        <div v-if="showCalendar" class="calendar-popover">
          <MaterialCalendar
            :selectedStartDate="props.startDate"
            :selectedEndDate="props.endDate"
            :activityDates="activityDates"
            @range-select="onCalendarRangeSelect"
            @close="showCalendar = false"
          />
        </div>
      </div>
      <div class="timeline-visual-wrapper" v-if="locations.length > 0">
          <!-- Gaps Markers -->
          <div class="timeline-gaps">
             <div 
               v-for="(gap, idx) in timelineGaps" 
               :key="`gap-${idx}`"
               class="gap-marker"
               :style="{ left: gap.left + '%' }"
               :title="`Gap: ${gap.duration}`"
             >
                <div class="gap-line"></div>
                <span class="gap-label">{{ gap.duration }}</span>
             </div>
          </div>

          <!-- Refuel Markers -->
          <div class="timeline-refuels">
             <div 
               v-for="(refuel, idx) in timelineRefuels" 
               :key="`refuel-${idx}`"
               class="refuel-marker"
               :style="{ left: refuel.left + '%' }"
               :title="`Rifornimento: ${refuel.amount}`"
             >
                <div class="refuel-icon">⛽</div>
                <span class="refuel-label">{{ refuel.amount }}</span>
             </div>
          </div>

          <!-- Color Bar -->
          <div class="timeline-bar">
             <div 
               v-for="(seg, idx) in timelineSegments" 
               :key="`seg-${idx}`"
               class="status-segment"
               :class="seg.type"
               :style="{ width: seg.width + '%' }"
             ></div>
          </div>

          <!-- Ruler (Time & Distance) -->
          <div class="timeline-ruler">
              <div 
                v-for="(tick, idx) in timelineTicks" 
                :key="`tick-${idx}`"
                class="ruler-tick"
                :style="{ left: tick.left + '%' }"
              >
                  <span class="tick-time">{{ tick.time }}</span>
                  <div class="tick-mark"></div>
                  <span class="tick-dist">{{ tick.dist }}</span>
              </div>
          </div>

          <!-- Slider (overlay) -->
          <input
            type="range"
            min="0"
            :max="locations.length - 1"
            v-model.number="playbackIndex"
            class="timeline-slider"
            @input="updatePlaybackMarker"
          />
      </div>
      
      <!-- Playback Controls - Minimal 3 buttons -->
      <div class="playback-controls">
        <button @click="restartPlayback" class="control-btn" title="Restart">
          🔄
        </button>
        <button @click="togglePlayback" class="control-btn" :class="{ 'playing': isPlaying }" :title="isPlaying ? 'Pause' : 'Play'">
          {{ isPlaying ? '⏸' : '▶' }}
        </button>
        <button @click="cycleSpeed" class="control-btn" title="Change speed">
          x{{ playbackSpeed }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  //cambio per deploy, cambi da effettuare
import {
  ref,
  onMounted,
  watch,
  nextTick,
  onBeforeUnmount,
  onUpdated,
  computed,
} from "vue";
import MaterialCalendar from "../ui/MaterialCalendarRange.vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix per le icone di Leaflet
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
// import MaterialCalendarRange from "../ui/MaterialCalendarRange.vue";

const props = defineProps<{
  locations: Array<{
    lat: number;
    lng: number;
    timestamp: number; // ← Cambia da `number` a `number | string` se i timestamp sono stringhe
    speed?: number;
    rpm?: number;
    fuel?: number;
  }>;
  loading: boolean;
  vin?: string;
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD 
  timeRange?: { start: number; end: number } | null;
}>();

const emit = defineEmits(['date-change']);

const mapContainer = ref<HTMLElement>();
const map = ref<any>(null);
const routeLayer = ref<any>(null);
const markersLayer = ref<any>(null);
const playbackMarker = ref<any>(null);
const tileLayer = ref<any>(null);
const isSatellite = ref(false);
const isMapInitialized = ref(false);
const playbackIndex = ref(0);
const isSnapping = ref(false); // Can be kept for loading state
const isSnapped = ref(false);
const isFollowing = ref(false);
const hasFittedBounds = ref(false);
const showCalendar = ref(false);
let updateTimeout: any = null;

// Playback controls
const isPlaying = ref(false);
const playbackSpeed = ref(0.2); // Start with 0.2x speed
const speedOptions = [0.2, 0.5, 1, 2, 4, 8, 16];
let playbackInterval: any = null;

// SVG base64 corretti
// SVG base64 corretti per marker di inizio (VERDE)
const startIconSvg =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSIxMCIgZmlsbD0iIzAwQjIwMCIgc3Ryb2tlPSIjRkZGRkZGIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSI0IiBmaWxsPSIjRkZGRkZGIi8+Cjwvc3ZnPgo=";

// SVG base64 corretti per marker di fine (ROSSO)
const endIconSvg =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSIxMCIgZmlsbD0iI0Y0MzM2IiBzdHJva2U9IiNGRkZGRkYiIHN0cm9rZS13aWR0aD0iMiIvPgogIDxjaXJjbGUgY3g9IjEyIiBjeT0iMTIiIHI9IjQiIGZpbGw9IiNGRkZGRkYiLz4KPC9zdmc+Cg==";

// Configura icone di default
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetinaUrl,
  iconUrl: iconUrl,
  shadowUrl: shadowUrl,
});

// Crea icone personalizzate
const startIcon = L.icon({
  iconUrl: startIconSvg,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

const endIcon = L.icon({
  iconUrl: endIconSvg,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

const playbackIconSvg = 
  `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 22L12 18L22 22L12 2Z" fill="#2196F3" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>
   </svg>`;

// Funzione helper per creare l'icona ruotata
function createRotatedIcon(angle: number) {
  return L.divIcon({
    className: 'playback-marker-icon', // Classe vuota o personalizzata
    html: `<div style="transform: rotate(${angle}deg); width: 24px; height: 24px;">${playbackIconSvg}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12], // Centro dell'icona
    popupAnchor: [0, -12]
  });
}

// COMPUTED per ultima posizione - SEMPRE AGGIORNATA
const latestLocation = computed(() => {
  if (!props.locations || props.locations.length === 0) return null;
  // Se stiamo riproducendo, mostra quella corrente, altrimenti l'ultima
  return props.locations[playbackIndex.value];
});

// Calcolo segmenti colorati timeline
const timelineSegments = computed(() => {
    if (!props.locations || props.locations.length === 0) return [];
    
    const segments = [];
    let currentType = '';
    let count = 0;
    
    for (const loc of props.locations) {
        // Determina stato
        const spd = (loc.speed || 0);
        // Fix GPS Drift: speed < 5 treated as 0 logic is consistent with map tooltip
        const speed = spd < 5 ? 0 : spd;
        const rpm = (loc.rpm !== undefined ? loc.rpm : 0);
        let type = 'parking'; // blue
        
        if (speed >= 5) {
            type = 'moving'; // green
        } else if (rpm > 0) {
            type = 'idle'; // yellow
        }
        
        if (type !== currentType) {
            if (count > 0) {
                segments.push({ type: currentType, count });
            }
            currentType = type;
            count = 1;
        } else {
            count++;
        }
    }
    // Push last
    if (count > 0) {
        segments.push({ type: currentType, count });
    }
    
    // Calcola width %
    const total = props.locations.length;
    return segments.map(s => ({
        type: s.type,
        width: (s.count / total) * 100
    }));
});

// Calcolo GAPS temporali (> 30 min)
const timelineGaps = computed(() => {
    if (!props.locations || props.locations.length < 2) return [];
    const gaps = [];
    const GAP_THRESHOLD = 30 * 60; // 30 minuti in secondi
    
    for (let i = 0; i < props.locations.length - 1; i++) {
        const curr = props.locations[i];
        const next = props.locations[i+1];
        
        // Gestione timestamp string/number
        const t1 = typeof curr.timestamp === 'string' ? parseInt(curr.timestamp) : curr.timestamp;
        const t2 = typeof next.timestamp === 'string' ? parseInt(next.timestamp) : next.timestamp;
        
        const diff = t2 - t1;
        
        if (diff > GAP_THRESHOLD) {
            // Found a gap
            const hours = Math.floor(diff / 3600);
            const mins = Math.floor((diff % 3600) / 60);
            let label = '';
            if (hours > 0) label += `${hours}h `;
            label += `${mins}m`;
            
            gaps.push({
                left: ((i + 0.5) / props.locations.length) * 100, // Position between points
                duration: label
            });
        }
    }
    return gaps;
});

// Calcolo Rifornimenti (Refuels)
const timelineRefuels = computed(() => {
    if (!props.locations || props.locations.length < 2) return [];
    const refuels = [];
    const FUEL_RISE_THRESHOLD = 5; // Increase > 5% considered refuel
    
    for (let i = 1; i < props.locations.length; i++) {
        const prev = props.locations[i-1];
        const curr = props.locations[i];
        
        if (prev.fuel !== undefined && curr.fuel !== undefined) {
             const rise = curr.fuel - prev.fuel;
             if (rise > FUEL_RISE_THRESHOLD) {
                 refuels.push({
                     left: (i / props.locations.length) * 100,
                     amount: `+${Math.round(rise)}%`
                 });
             }
        }
    }
    return refuels;
});

// Calcolo Cumulative Distances (computed per efficienza)
const cumulativeDistances = computed(() => {
    if (!props.locations || props.locations.length === 0) return [];
    const dists = [0];
    let total = 0;
    for (let i = 1; i < props.locations.length; i++) {
        const prev = props.locations[i-1];
        const curr = props.locations[i];
        if (prev.lat && prev.lng && curr.lat && curr.lng) {
            total += getDistanceFromLatLonInKm(prev.lat, prev.lng, curr.lat, curr.lng);
        }
        dists.push(total);
    }
    return dists;
});

// Ruler Ticks (Time & Km)
const timelineTicks = computed(() => {
    if (!props.locations || props.locations.length === 0) return [];
    const ticks = [];
    const COUNT = 6; // Numero di ticks (es. 0%, 20%, 40%, 60%, 80%, 100%)
    
    for (let i = 0; i < COUNT; i++) {
        const percent = (i / (COUNT - 1));
        const index = Math.floor(percent * (props.locations.length - 1));
        const loc = props.locations[index];
        
        if (loc) {
            // Time
             const dateObj = new Date(typeof loc.timestamp === 'string' ? parseInt(loc.timestamp)*1000 : loc.timestamp * 1000);
             const timeStr = dateObj.toLocaleTimeString("it-IT", { hour: '2-digit', minute: '2-digit' });
             
            // Dist
            const dist = cumulativeDistances.value[index] || 0;
            
            ticks.push({
                left: percent * 100,
                time: timeStr,
                dist: dist > 0 ? `${dist.toFixed(0)} km` : '0 km'
            });
        }
    }
    return ticks;
});

// WRAPPER SICURO PER OPERAZIONI LEAFLET
function safeLeafletOperation(operation: () => void, operationName: string) {
  try {
    operation();
  } catch (error) {
    console.warn(`⚠️ [RouteMap] Leaflet error in ${operationName}:`, error);
    // Se c'è un errore, forziamo la ricreazione al prossimo aggiornamento
    isMapInitialized.value = false;
  }
}

// PULIZIA MIGLIORATA
function destroyMap() {
  // console.log("💥 [RouteMap] Destroying map completely");

  try {
    if (map.value) {
      // Ferma tutte le animazioni
      map.value.stop();

      // Rimuovi tutti gli event listener
      map.value.off();

      // Rimuovi tutti i layer
      map.value.eachLayer((layer: any) => {
        if (layer && layer.remove) {
          try {
            layer.remove();
          } catch (e) {
            // Ignora errori durante la pulizia
          }
        }
      });

      // Rimuovi la mappa
      map.value.remove();
      map.value = null;
    }

    // Pulisci i riferimenti
    routeLayer.value = null;
    markersLayer.value = null;
    playbackMarker.value = null;
    tileLayer.value = null;

    // Pulisci il container
    // Riga 171-172 (nella destroyMap):
    if (mapContainer.value && (mapContainer.value as any)._leaflet) {
      delete (mapContainer.value as any)._leaflet;
    }
  } catch (error) {
    console.warn("⚠️ [RouteMap] Error during map destruction:", error);
  } finally {
    isMapInitialized.value = false;
  }
}

// WATCH con debounce per evitare aggiornamenti troppo rapidi
watch(
  () => props.locations,
  (newLocations) => {
    // console.log("🚀 [RouteMap] Watch locations triggered:", {
    //   newLocationsLength: newLocations?.length,
    //   isMapInitialized: isMapInitialized.value,
    //   hasContainer: !!mapContainer.value,
    // });

    if (!newLocations || newLocations.length === 0) {
      console.log("🗑️ [RouteMap] Empty locations array received, clearing map");
      clearMap();
      return;
    }  // capire se mantenere questa logica poiche quando viene cancellata la mappa ci sono errori di sovrapposizione

    // Reset playback
    // Reset playback & snapped state
    // Don't reset playbackIndex strictly if we want to keep position? 
    // Usually on new data we want to seek to end? 
    // For now keep explicit reset logic but check if we should reset bounds
    if (newLocations && props.locations && Math.abs(newLocations.length - props.locations.length) > 10) {
        // If length changes significantly (e.g. data load), maybe reset bounds? 
        // For now, rely on explicit loading watcher.
    }
    
    playbackIndex.value = newLocations ? newLocations.length - 1 : 0;
    isSnapped.value = false; // Reset snapped state on new data
    
    // Cancella eventuali update in attesa
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    // Debounce di 50ms
    updateTimeout = setTimeout(() => {
      if (newLocations?.length) {
        const processMapUpdate = async () => {
          await nextTick();

          if (isMapInitialized.value && mapContainer.value) {
            // console.log("🔄 [RouteMap] Map initialized, updating...");
            updateMap();
          } else if (mapContainer.value && !isMapInitialized.value) {
            // console.log("📍 [RouteMap] Map not initialized, initializing...");
            initMap();
          } else {
            // console.log("⏱️ [RouteMap] Container not ready, waiting...");
            setTimeout(() => {
              if (mapContainer.value && !isMapInitialized.value) {
                initMap();
              }
            }, 100);
          }
        };

        processMapUpdate();
      } else if (isMapInitialized.value) {
        // console.log("🗑️ [RouteMap] Empty locations, clearing map");
        clearMap();
      }
    }, 50);
  },
  { deep: true }
);

// Calendar logic
const currentDateStr = computed(() => {
  if (props.startDate) return props.startDate;
  if (props.locations && props.locations.length > 0) {
    const ts = props.locations[0].timestamp;
    const d =
      typeof ts === "string"
        ? new Date(parseInt(ts, 10) * 1000)
        : new Date(ts * 1000);

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
});

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit'
  });
}

const activityDates = computed(() => {
    // Return array of dates that have activity.
    // For now, just current loaded date.
    return [currentDateStr.value];
});

function toggleCalendar() {
    showCalendar.value = !showCalendar.value;
}

function onCalendarRangeSelect(range: { start: string, end: string }) {
  emit('date-change', range);  // Emetti l'intero oggetto range
  showCalendar.value = false;
}

// Watch per loading
watch(
  () => props.loading,
  (newLoading) => {
    if (!newLoading && props.locations?.length && mapContainer.value) {
      // console.log("📦 [RouteMap] Loading finished, checking map visibility");
      
      setTimeout(() => {
        if (map.value && isMapInitialized.value) {
            // Se la mappa è già stata inizializzata (magari mentre era nascosta),
            // dobbiamo invalidare la dimensione e rifare fitBounds
            map.value.invalidateSize();
            fitBounds();
        } else if (!isMapInitialized.value) {
            initMap();
        }
      }, 100);
    }
  }
);

onMounted(() => {
  // console.log("🏗️ [RouteMap] Component mounted");

  // ResizeObserver for responsive map resizing
  const resizeObserver = new ResizeObserver(() => {
    if (map.value) {
       map.value.invalidateSize();
    }
  });
  
  if (mapContainer.value) {
    resizeObserver.observe(mapContainer.value);
  }
  
  // Clean up observer on unmount
  onBeforeUnmount(() => {
     resizeObserver.disconnect();
  });
  
  nextTick(() => {
    // console.log("⏳ [RouteMap] After mount nextTick:", {
    //   hasContainer: !!mapContainer.value,
    //   hasLocations: props.locations?.length,
    //   loading: props.loading,
    // });

    if (mapContainer.value && props.locations?.length && !props.loading) {
      // console.log("✅ [RouteMap] Conditions met for initMap on mount");
      setTimeout(() => {
        if (mapContainer.value && !isMapInitialized.value) {
          initMap();
        }
      }, 100);
    } else {
      console.log("⏸️ [RouteMap] Waiting for conditions:", {
        missingContainer: !mapContainer.value,
        missingLocations: !props.locations?.length,
        stillLoading: props.loading,
      });
    }
  });
});

function initMap() {
  safeLeafletOperation(() => {
    // console.log("🗺️ [RouteMap] initMap called", {
    //   hasContainer: !!mapContainer.value,
    //   hasLocations: props.locations?.length,
    //   alreadyInitialized: isMapInitialized.value,
    // });

    if (!mapContainer.value) {
      console.error(
        "❌ [RouteMap] initMap FAILED: No container element found!"
      );
      return;
    }

    if (!props.locations?.length || isMapInitialized.value) {
      // console.log(
      //   "❌ [RouteMap] initMap aborted - no locations or already initialized"
      // );
      return;
    }

    // console.log("✅ [RouteMap] Starting map initialization...");

    // Assicurati che non ci siano mappe esistenti
    destroyMap();

    // Crea la mappa
    // console.log("🆕 [RouteMap] Creating new map on container");
    map.value = L.map(mapContainer.value, {
      fadeAnimation: false, // Disabilita animazioni iniziali
      zoomAnimation: false,
      scrollWheelZoom: false // DISABLE SCROLL WHEEL ZOOM
    }).setView([props.locations[0].lat, props.locations[0].lng], 13);

    // console.log("📍 [RouteMap] Map created at position:", props.locations[0]);

    // Aggiungi layer base
    tileLayer.value = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }
    ).addTo(map.value);

    // console.log("🌍 [RouteMap] Tile layer added");

    // Inizializza layer
    markersLayer.value = L.layerGroup().addTo(map.value);

    // console.log("📍 [RouteMap] Layer group created");

    isMapInitialized.value = true;

    // console.log("✅ [RouteMap] Map initialization complete");

    // Forza invalidateSize
    setTimeout(() => {
      if (map.value) {
        try {
          map.value.invalidateSize({ animate: false });
          // console.log("📏 [RouteMap] Map size invalidated");
        } catch (error) {
          console.error("❌ [RouteMap] Error during invalidateSize:", error);
        }
      }
    }, 200);

    // Aggiorna la mappa con i dati
    updateMap();
  }, "initMap");
}

function updateMap() {
  safeLeafletOperation(() => {
    // console.log("🔄 [RouteMap] updateMap called", {
    //   hasMap: !!map.value,
    //   hasLocations: props.locations?.length,
    //   hasMarkersLayer: !!markersLayer.value,
    //   latestLocation: latestLocation.value,
    // });

    if (!map.value || !props.locations?.length) {
      // console.log("❌ [RouteMap] updateMap aborted - missing map or locations");
      return;
    }

    // Pulisci layer precedenti
    if (routeLayer.value) {
      // console.log("🧹 [RouteMap] Removing old route layer");
      map.value.removeLayer(routeLayer.value);
      routeLayer.value = null;
    }

    if (markersLayer.value) {
      markersLayer.value.clearLayers();
      // console.log("🧹 [RouteMap] Cleared markers layer");
    } else {
      markersLayer.value = L.layerGroup().addTo(map.value);
      // console.log("📍 [RouteMap] Created new markers layer");
    }

    // Crea array di coordinate per la rotta
    const routeCoordinates: [number, number][] = props.locations
      .filter((loc) => loc.lat && loc.lng && !isNaN(loc.lat) && !isNaN(loc.lng))
      .map((loc) => [loc.lat, loc.lng]);

    // console.log(
    //   "📍 [RouteMap] Route coordinates:",
    //   routeCoordinates.length,
    //   "points"
    // );

    if (routeCoordinates.length < 2) {
      console.warn("⚠️ [RouteMap] Insufficient coordinates to draw route");
      return;
    }

    // Disegna la rotta
    // console.log("🎨 [RouteMap] Drawing route polyline");
    routeLayer.value = L.polyline(routeCoordinates, {
      color: "#4caf50",
      weight: 4,
      opacity: 0.7,
      lineJoin: "round",
    }).addTo(map.value);

    // console.log("✅ [RouteMap] Route drawn successfully");

    // Aggiungi marker di inizio
    const startCoord = routeCoordinates[0];
    // console.log("📍 [RouteMap] Start marker at:", startCoord);
    const startMarker = L.marker(startCoord, {
      icon: startIcon,
    }).bindPopup(
      `<b>Partenza</b><br>${formatTimestamp(props.locations[0].timestamp)}${
        props.vin ? `<br>VIN: ${props.vin}` : ""
      }`
    );

    // Aggiungi marker di fine - USA LA COMPUTED latestLocation
    const endCoord = routeCoordinates[routeCoordinates.length - 1];
    // console.log(
    //   "📍 [RouteMap] End marker at:",
    //   endCoord,
    //   "Latest location timestamp:",
    //   latestLocation.value?.timestamp
    // );

    const endMarker = L.marker(endCoord, {
      icon: endIcon,
    }).bindPopup(
      `<b>Ultima posizione</b><br>${formatTimestamp(
        latestLocation.value?.timestamp
      )}${props.vin ? `<br>VIN: ${props.vin}` : ""}`
    );

    // Aggiungi i marker al layer group
    startMarker.addTo(markersLayer.value);
    endMarker.addTo(markersLayer.value);

    // console.log("📍 [RouteMap] Markers added to layer group");

    // Aggiorna marker playback
    updatePlaybackMarker();
    
    // Centra la mappa sulla rotta
    // Centra la mappa sulla rotta SOLO se non l'abbiamo già fatto o se richiesto
    if (!hasFittedBounds.value) {
        fitBounds();
        hasFittedBounds.value = true;
    }
    // console.log("✅ [RouteMap] Map update complete");
  }, "updateMap");
}

function updatePlaybackMarker() {
  if (!map.value || !props.locations || !props.locations[playbackIndex.value]) return;
  
  const loc = props.locations[playbackIndex.value];
  if (!loc.lat || !loc.lng) return;

  // Calcolo bearing
  let angle = 0;
  // Se c'è un punto successivo, calcola l'angolo verso di esso
  if (playbackIndex.value < props.locations.length - 1) {
      const nextLoc = props.locations[playbackIndex.value + 1];
      if (nextLoc.lat && nextLoc.lng) {
          angle = calculateBearing(loc.lat, loc.lng, nextLoc.lat, nextLoc.lng);
      }
  } 
  // Altrimenti guarda al punto precedente (utile per l'ultimo frame)
  else if (playbackIndex.value > 0) {
      const prevLoc = props.locations[playbackIndex.value - 1];
       if (prevLoc.lat && prevLoc.lng) {
          angle = calculateBearing(prevLoc.lat, prevLoc.lng, loc.lat, loc.lng);
      }
  }

  const icon = createRotatedIcon(angle);
  
  // STATO
  // Filtro GPS drift: se velocità < 5 km/h, consideriamo fermo
  const rawSpeed = loc.speed || 0;
  const speed = rawSpeed < 5 ? 0 : rawSpeed;
  const rpm = loc.rpm !== undefined ? loc.rpm : 0;
  
  let statusIcon = "🅿️"; // Default Parking
  let statusText = "Parcheggiato";
  
  if (speed > 0) {
      statusIcon = "🟢"; // In moto
      statusText = "In Movimento";
  } else if (rpm > 0) {
      statusIcon = "⚠️"; // Sosta (motore acceso, fermo) o "🟡"
      statusText = "Sosta (Motore Acceso)";
  } else {
      statusIcon = "🅿️"; // Parking
      statusText = "Parcheggiato";
  }

  // Label HTML
  const dateObj = new Date(typeof loc.timestamp === 'string' ? parseInt(loc.timestamp)*1000 : loc.timestamp * 1000);
  const dateStr = dateObj.toLocaleDateString("it-IT"); // e.g. 29/12/2025
  const timeStr = dateObj.toLocaleTimeString("it-IT"); // e.g. 13:19:33
  
  const labelContent = `
    <div class="marker-label-content">
        <div class="row-main">
            <span class="icon">${statusIcon}</span>
            <span class="speed">${Math.round(speed)} km/h</span>
        </div>
        <div class="row-sub">
            <span class="date">${dateStr}</span>
            <span class="time">${timeStr}</span>
        </div>
    </div>
  `;

  if (playbackMarker.value) {
    playbackMarker.value.setLatLng([loc.lat, loc.lng]);
    playbackMarker.value.setIcon(icon); 
    
    // Usa tooltip permanente invece di popup per "etichetta sopra"
    if (playbackMarker.value.getTooltip()) {
        playbackMarker.value.setTooltipContent(labelContent);
    } else {
        playbackMarker.value.bindTooltip(labelContent, { 
            permanent: true, 
            direction: 'top', 
            className: 'playback-tooltip-custom',
            offset: [0, -20]
        });
    }
  } else {
    playbackMarker.value = L.marker([loc.lat, loc.lng], {
      icon: icon,
      zIndexOffset: 1000 
    }).addTo(map.value);
    
    playbackMarker.value.bindTooltip(labelContent, { 
        permanent: true, 
        direction: 'top', 
        className: 'playback-tooltip-custom',
        offset: [0, -20]
    });
  }

  // Auto-follow logic
  if (isFollowing.value && map.value) {
      map.value.panTo([loc.lat, loc.lng]);
  }
}

// ============ PLAYBACK CONTROLS ============
function togglePlayback() {
  if (isPlaying.value) {
    pausePlayback();
  } else {
    startPlayback();
  }
}

function startPlayback() {
  if (!props.locations || props.locations.length === 0) return;
  
  isPlaying.value = true;
  
  // Se siamo alla fine, ricomincia dall'inizio
  if (playbackIndex.value >= props.locations.length - 1) {
    playbackIndex.value = 0;
  }
  
  let lastTimestamp = performance.now();
  const baseSpeed = 50; // punti al secondo base
  const speed = baseSpeed * playbackSpeed.value;
  
  function animate(currentTime: number) {
    if (!isPlaying.value) return;
    
    const deltaTime = (currentTime - lastTimestamp) / 1000; // secondi
    lastTimestamp = currentTime;
    
    // Calcola quanti punti avanzare (può essere frazionario per interpolazione)
    const pointsToAdvance = speed * deltaTime;
    
    // Aggiorna l'indice con interpolazione
    const newIndex = playbackIndex.value + pointsToAdvance;
    
    if (newIndex >= props.locations.length - 1) {
      // Fine dell'animazione
      playbackIndex.value = props.locations.length - 1;
      updatePlaybackMarker();
      pausePlayback();
      return;
    }
    
    playbackIndex.value = newIndex;
    updatePlaybackMarkerSmooth();
    
    playbackInterval = requestAnimationFrame(animate);
  }
  
  playbackInterval = requestAnimationFrame(animate);
}

function updatePlaybackMarkerSmooth() {
  if (!props.locations || props.locations.length === 0) return;
  
  const index = Math.floor(playbackIndex.value);
  const nextIndex = Math.min(index + 1, props.locations.length - 1);
  const fraction = playbackIndex.value - index;
  
  const current = props.locations[index];
  const next = props.locations[nextIndex];
  
  // Interpolazione lineare tra i due punti
  const lat = current.lat + (next.lat - current.lat) * fraction;
  const lng = current.lng + (next.lng - current.lng) * fraction;
  const speed = (current.speed || 0) + ((next.speed || 0) - (current.speed || 0)) * fraction;
  
  // Calcola bearing per rotazione
  let bearing = 0;
  if (nextIndex > index) {
    bearing = calculateBearing(current.lat, current.lng, next.lat, next.lng);
  }
  
  const icon = createRotatedIcon(bearing);
  
  // Determina stato e icona
  let statusIcon = "🚗";
  let statusText = "In movimento";
  
  if (speed < 1) {
      statusIcon = "🅿️";
      statusText = "Parcheggiato";
  }
  
  // Label HTML
  const dateObj = new Date(typeof current.timestamp === 'string' ? parseInt(current.timestamp)*1000 : current.timestamp * 1000);
  const dateStr = dateObj.toLocaleDateString("it-IT");
  const timeStr = dateObj.toLocaleTimeString("it-IT");
  
  const labelContent = `
    <div class="marker-label-content">
        <div class="row-main">
            <span class="icon">${statusIcon}</span>
            <span class="speed">${Math.round(speed)} km/h</span>
        </div>
        <div class="row-sub">
            <span class="date">${dateStr}</span>
            <span class="time">${timeStr}</span>
        </div>
    </div>
  `;
  
  if (playbackMarker.value) {
    playbackMarker.value.setLatLng([lat, lng]);
    playbackMarker.value.setIcon(icon);
    
    if (playbackMarker.value.getTooltip()) {
        playbackMarker.value.setTooltipContent(labelContent);
    } else {
        playbackMarker.value.bindTooltip(labelContent, { 
            permanent: true, 
            direction: 'top', 
            className: 'playback-tooltip-custom',
            offset: [0, -20]
        });
    }
  } else {
    playbackMarker.value = L.marker([lat, lng], {
      icon: icon,
      zIndexOffset: 1000 
    }).addTo(map.value);
    
    playbackMarker.value.bindTooltip(labelContent, { 
        permanent: true, 
        direction: 'top', 
        className: 'playback-tooltip-custom',
        offset: [0, -20]
    });
  }
  
  // Auto-follow logic
  if (isFollowing.value && map.value) {
      map.value.panTo([lat, lng]);
  }
}

function pausePlayback() {
  isPlaying.value = false;
  if (playbackInterval) {
    cancelAnimationFrame(playbackInterval);
    playbackInterval = null;
  }
}

function restartPlayback() {
  pausePlayback();
  playbackIndex.value = 0;
  updatePlaybackMarker();
}

function setPlaybackSpeed(speed: number) {
  const wasPlaying = isPlaying.value;
  if (wasPlaying) {
    pausePlayback();
  }
  
  playbackSpeed.value = speed;
  
  if (wasPlaying) {
    startPlayback();
  }
}

function cycleSpeed() {
  const currentIndex = speedOptions.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speedOptions.length;
  setPlaybackSpeed(speedOptions[nextIndex]);
}


// Funzione calcolo bearing
function calculateBearing(startLat: number, startLng: number, destLat: number, destLng: number) {
  const startLatRad = (startLat * Math.PI) / 180;
  const startLngRad = (startLng * Math.PI) / 180;
  const destLatRad = (destLat * Math.PI) / 180;
  const destLngRad = (destLng * Math.PI) / 180;

  const y = Math.sin(destLngRad - startLngRad) * Math.cos(destLatRad);
  const x =
    Math.cos(startLatRad) * Math.sin(destLatRad) -
    Math.sin(startLatRad) * Math.cos(destLatRad) * Math.cos(destLngRad - startLngRad);

  const brngRad = Math.atan2(y, x);
  const brngDeg = (brngRad * 180) / Math.PI;

  return (brngDeg + 360) % 360; // Normalizza 0-360
  // Auto-snap to roads (default behavior requested)
  if (!isSnapped.value && !isSnapping.value) {
      snapToRoads();
  }
}

function clearMap() {
  safeLeafletOperation(() => {
    if (routeLayer.value && map.value) {
      map.value.removeLayer(routeLayer.value);
      routeLayer.value = null;
    }

    if (markersLayer.value) {
      markersLayer.value.clearLayers();
    }

    if (playbackMarker.value) {
      map.value.removeLayer(playbackMarker.value);
      playbackMarker.value = null;
    }
  }, "clearMap");
}

function fitBounds() {
  safeLeafletOperation(() => {
    if (!map.value || !routeLayer.value) return;

    const bounds = routeLayer.value.getBounds();
    if (bounds && typeof bounds.isValid === "function" && bounds.isValid()) {
      map.value.fitBounds(bounds, { padding: [50, 50] });
      // console.log("🎯 [RouteMap] Map fitted to bounds");
    } else {
      console.warn("⚠️ [RouteMap] Invalid bounds, cannot fit");
    }
  }, "fitBounds");
}

function toggleSatellite() {
  safeLeafletOperation(() => {
    if (!map.value) {
      console.warn("⚠️ [RouteMap] Cannot toggle satellite - no map");
      return;
    }

    isSatellite.value = !isSatellite.value;

    if (tileLayer.value) {
      map.value.removeLayer(tileLayer.value);
    }

    if (isSatellite.value) {
      tileLayer.value = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        {
          attribution: "© Esri",
          maxZoom: 19,
        }
      );
    } else {
      tileLayer.value = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
        }
      );
    }

    // Add new tile layer (Leaflet automatically puts it in the tile pane, below overlays)
    tileLayer.value.addTo(map.value);
    tileLayer.value.bringToBack(); // Ensure it stays behind route/markers

    // console.log(
    //   "🛰️ [RouteMap] Satellite mode:",
    //   isSatellite.value ? "ON" : "OFF"
    // );
  }, "toggleSatellite");
}

function calculateTotalDistance() {
  if (!props.locations || props.locations.length < 2) return 0;

  let totalDistance = 0;
  for (let i = 1; i < props.locations.length; i++) {
    const prev = props.locations[i - 1];
    const curr = props.locations[i];
    if (prev.lat && prev.lng && curr.lat && curr.lng) {
      totalDistance += calculateDistance(
        prev.lat,
        prev.lng,
        curr.lat,
        curr.lng
      );
    }
  }

  return totalDistance;
}

function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
    return 0;
  }

  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatTimestamp(timestamp: number | string | undefined) {
  if (!timestamp) return "N/D";

  // Converte stringa in numero se necessario
  const ts =
    typeof timestamp === "string" ? parseInt(timestamp, 10) : timestamp;

  // Controlla se è un timestamp valido
  if (isNaN(ts) || ts <= 0) return "N/D";

  return new Date(ts * 1000).toLocaleString("it-IT");
}

// OSRM Map Matching
// OSRM Routing (Robust Snap to Roads)
async function snapToRoads() {
  if (!props.locations || props.locations.length < 2) return;
  
  isSnapping.value = true;
  // console.log("🛣️ [RouteMap] Starting Snap to Roads (Routing Mode)...");

  try {
      // 1. Prepare coordinates (Waypoints)
      // OSRM Route demo limit is approx 25-50 waypoints (URL length limit mostly).
      // We diligently downsample to 25 points to ensure success.
      const MAX_WAYPOINTS = 25; 
      let waypoints = props.locations
        .filter(l => l.lat && l.lng)
        .map(l => [l.lng, l.lat]); // lon,lat

      if (waypoints.length > MAX_WAYPOINTS) {
          // console.log(`📉 [RouteMap] Downsampling from ${waypoints.length} to ${MAX_WAYPOINTS}`);
          const step = (waypoints.length - 1) / (MAX_WAYPOINTS - 1);
          const newWaypoints = [];
          for (let i = 0; i < MAX_WAYPOINTS; i++) {
              newWaypoints.push(waypoints[Math.round(i * step)]);
          }
          waypoints = newWaypoints;
      }

      // Format for URL: lon,lat;lon,lat;...
      const coordString = waypoints.map(c => c.join(',')).join(';');
      // Use ROUTE service instead of MATCH
      const url = `https://router.project-osrm.org/route/v1/driving/${coordString}?overview=full&geometries=geojson`;

      // 2. Call API
      const response = await fetch(url);
      if (!response.ok) throw new Error(`OSRM request failed: ${response.status}`);
      
      const data = await response.json();
      
      if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          // data.routes[0].geometry contains the full path geometry
          const geometry = data.routes[0].geometry;
          const latLngs = geometry.coordinates.map((c: any) => [c[1], c[0]]); // swap to lat,lon

          // 3. Update Map Route
          if (routeLayer.value && map.value) {
              map.value.removeLayer(routeLayer.value);
          }
          
          // console.log(`✅ [RouteMap] Drawing routed path with ${latLngs.length} points`);
          routeLayer.value = L.polyline(latLngs, {
              color: "#2196F3", // Blue for snapped route
              weight: 5,
              opacity: 0.8,
              lineJoin: "round",
          }).addTo(map.value);

          isSnapped.value = true;
      } else {
        console.warn("⚠️ [RouteMap] OSRM returned no routes", data);
      }

  } catch (e) {
      console.error("❌ [RouteMap] Snap to Roads error:", e);
      // Optional: Notify user via UI toast if available
  } finally {
      isSnapping.value = false;
  }
}

function centerOnMarker() {
    if (!map.value || !props.locations || !props.locations[playbackIndex.value]) return;
    const loc = props.locations[playbackIndex.value];
    if (loc.lat && loc.lng) {
        map.value.setView([loc.lat, loc.lng], 16); // Zoom level 16 (requested: 2 points back from 18)
    }
}

function toggleFollow() {
    isFollowing.value = !isFollowing.value;
    if (isFollowing.value) {
        centerOnMarker(); // Center immediately when enabled
    }
}

// Cleanup
onBeforeUnmount(() => {
  // console.log("👋 [RouteMap] Component unmounting");

  // Pulisci timeout
  if (updateTimeout) {
    clearTimeout(updateTimeout);
    updateTimeout = null;
  }
  
  // Cleanup playback interval
  if (playbackInterval) {
    cancelAnimationFrame(playbackInterval);
    playbackInterval = null;
  }

  // Distruggi la mappa
  destroyMap();
});

// Forza invalidateSize sugli update
// onUpdated deleted (caused infinite loop/spam)

function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
    Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
</script>

<style scoped>
.route-map {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
}

.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #222;
  border-bottom: 1px solid #333;
}

.map-header h3 {
  margin: 0;
  font-size: 18px;
  color: white;
}

.map-container {
  flex: 1;
  min-height: 400px;
  background: #2a2a2a;
  position: relative;
  overflow: hidden; /* Critical for keeping tiles contained */
}

/* Map Controls Overlay - positioned inside map */
.map-controls-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.map-overlay-btn {
  width: 36px;
  height: 36px;
  background: rgba(42, 42, 42, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.map-overlay-btn:hover {
  background: rgba(0, 143, 143, 0.95);
  border-color: rgba(0, 167, 167, 0.5);
  transform: scale(1.05);
}

.map-overlay-btn.active {
  background: rgba(0, 143, 143, 0.95);
  border-color: rgba(0, 167, 167, 0.8);
  box-shadow: 0 0 0 2px rgba(0, 143, 143, 0.3);
}

.map-loading,
.map-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #aaa;
  font-size: 16px;
  background: #2a2a2a;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #333;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.map-info {
  display: flex;
  justify-content: space-around;
  padding: 16px 20px;
  background: #222;
  border-top: 1px solid #333;
}

.info-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  color: #aaa;
}

.info-value {
  font-size: 16px;
  color: white;
  font-weight: bold;
}

.timeline-container {
  padding: 10px 20px;
  background: #222;
  border-top: 1px solid #333;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center; /* Align vertically center */
  margin-bottom: 8px;
  font-size: 14px;
  color: #ccc;
  position: relative; /* For popover positioning */
}

.timeline-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.calendar-btn {
    background: transparent;
    border: none;
    font-size: 16px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.2s;
}
.calendar-btn:hover {
    background: rgba(255,255,255,0.1);
}

.calendar-popover {
    position: absolute;
    bottom: 40px; /* Above header */
    left: 0;
    z-index: 1000;
}
.map-btn .btn-label {
    margin-left: 6px;
    font-size: 12px;
}

.map-btn.active {
    background: #2196F3;
    color: white;
    border-color: #1976D2;
}

/* TIMELINE STYLES */
/* Timeline Visuals */
.timeline-visual-wrapper {
  position: relative;
  height: 60px; /* Increased height for labels */
  margin-top: 25px; /* Spazio per gap labels sopra */
  margin-bottom: 25px; /* Spazio per km labels sotto */
  width: 100%;
}

.timeline-gaps {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 5;
}

.gap-marker {
    position: absolute;
    top: -10px; /* Sposta sopra la barra */
    bottom: -10px; 
    width: 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.gap-line {
    width: 2px;
    height: 100%; /* Linea verticale */
    background-color: #ff3b30;
    opacity: 0.8;
}

.gap-label {
    position: absolute;
    top: -24px; /* Etichetta sopra la gap */
    white-space: nowrap;
    font-size: 11px;
    color: #ff3b30;
    font-weight: bold;
    background: rgba(0,0,0,0.8);
    padding: 2px 5px;
    border-radius: 4px;
    transform: translateX(-50%); /* Center label */
    left: 0px; /* Center alignment */
}

/* REFUEL MARKERS */
.timeline-refuels {
    position: absolute;
    top: 0; 
    left: 0; 
    width: 100%; 
    height: 100%;
    pointer-events: none;
    z-index: 6;
}

.refuel-marker {
    position: absolute;
    top: -28px; /* Icona sopra la timeline */
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
}

.refuel-icon {
    font-size: 14px;
    background: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
    border: 1px solid #4CAF50;
    cursor: help;
}

.refuel-label {
    font-size: 9px;
    color: #fff;
    background: #4CAF50;
    padding: 1px 3px;
    border-radius: 3px;
    margin-top: 2px;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.timeline-bar {
  position: absolute;
  top: 20px; /* Centrato verticalmente rispetto allo spazio allocato */
  left: 0;
  width: 100%;
  height: 8px; /* Thinner elegant bar */
  background: #333;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
}

.status-segment {
    height: 100%;
}
.status-segment.moving { background-color: #4caf50; }
.status-segment.idle { background-color: #ffc107; }
.status-segment.parking { background-color: #2196f3; }

/* Ruler */
.timeline-ruler {
    position: absolute;
    top: 20px; /* Align with bar */
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.ruler-tick {
    position: absolute;
    transform: translateX(-50%); /* Center strictly on point */
    display: flex;
    flex-direction: column;
    align-items: center;
}

.tick-mark {
    width: 1px;
    height: 14px; /* Ticks che sbordano un po' dalla barra */
    background: rgba(255, 255, 255, 0.3);
    margin: -3px 0; /* Centra verticalmente sulla barra di 8px */
}

.tick-time {
    position: absolute;
    top: -20px; /* Time ABOVE bar */
    font-size: 10px;
    color: #aaa;
    white-space: nowrap;
}

.tick-dist {
    position: absolute;
    top: 14px; /* Dist BELOW bar */
    font-size: 10px;
    color: #888;
    white-space: nowrap;
}

.timeline-slider {
  position: absolute;
  top: 13px; /* Align thumb with bar */
  left: -1%; 
  width: 102%;
  height: 20px;
  margin: 0;
  padding: 0;
  background: transparent;
  z-index: 10;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}

/* Custom Thumb */
.timeline-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #2196F3;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  cursor: grab;
  margin-top: 0px; 
}

/* Firefox Thumb */
.timeline-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #2196F3;
  cursor: grab;
}
</style>

<style>
/* CLASSE GLOBALE PER TOOLTIP CUSTOM (fuori da scoped per Leaflet) */
.playback-tooltip-custom {
    background: rgba(30, 30, 30, 0.9) !important;
    border: 1px solid #444 !important;
    border-radius: 6px !important;
    color: white !important;
    padding: 0 !important;
    box-shadow: 0 4px 10px rgba(0,0,0,0.5) !important;
    font-family: 'Inter', sans-serif !important;
}
.playback-tooltip-custom .leaflet-tooltip-content {
    margin: 0 !important;
}
/* Freccetta tooltip */
.playback-tooltip-custom::before {
    border-top-color: rgba(30, 30, 30, 0.9) !important;
}

.marker-label-content {
    display: flex;
    flex-direction: column;
    padding: 6px 10px;
    text-align: center;
    min-width: 100px;
}

.marker-label-content .row-main {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-bottom: 2px;
}

.marker-label-content .icon {
    font-size: 16px;
}

.marker-label-content .speed {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
}

.marker-label-content .row-sub {
    display: flex;
    gap: 6px;
    justify-content: center;
    font-size: 11px;
    color: #aaa;
    white-space: nowrap;
}

/* ============ PLAYBACK CONTROLS - MINIMAL ============ */
.playback-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px;
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #2a2f34;
  border: 1px solid #3a3f44;
  border-radius: 4px;
  color: #e6e6e6;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-weight: 600;
}

.control-btn:hover {
  background: #3a3f44;
  border-color: #008f8f;
  transform: scale(1.05);
}

.control-btn:active {
  transform: scale(0.95);
}

.control-btn.playing {
  background: #008f8f;
  border-color: #00a7a7;
  color: #fff;
}

.control-btn.playing:hover {
  background: #00a7a7;
  border-color: #00bfbf;
}
</style>
